<script setup lang="ts">
import { useI18n } from "vue-i18n";

import { hasValue } from "@/Utils/Objects";

const { t } = useI18n();

const rating = defineModel<number | undefined>();

const COLOR_MAP: Record<number, { border: string; bg: string }> = {
  1: { border: "semantic-error", bg: "semantic-error-low" },
  2: { border: "semantic-error", bg: "semantic-error-low" },
  3: { border: "semantic-error", bg: "semantic-error-low" },
  4: { border: "semantic-error", bg: "semantic-error-low" },
  5: { border: "semantic-error", bg: "semantic-error-low" },
  6: { border: "semantic-error", bg: "semantic-error-low" },
  7: { border: "semantic-warning", bg: "semantic-warning-low" },
  8: { border: "semantic-warning", bg: "semantic-warning-low" },
  9: { border: "semantic-success", bg: "semantic-success-low" },
  10: { border: "semantic-success", bg: "semantic-success-low" },
};

const getStylesByIndex = (index: number): object => {
  const colors = COLOR_MAP[index];

  if (!hasValue(colors)) return {};

  const styles = {
    [`bc-${colors.border}-important`]: true,
  };

  if (hasValue(rating.value) && rating.value >= index) {
    styles[`bg-${colors.border}-important`] = true;
  } else {
    styles[`bg-${colors.bg}-important`] = true;
  }

  return styles;
};

const handleSelect = (value: number) => {
  rating.value = rating.value === value ? undefined : value;
};
</script>

<template>
  <div class="number_rating" role="radiogroup" aria-label="Rating">
    <div v-for="value in 10" :key="value" class="number_rating__cell">
      <button
        class="number_rating__cell__item"
        type="button"
        :class="{
          'number_rating__cell__item--active': hasValue(rating) && rating >= value,
          ...getStylesByIndex(value),
        }"
        :aria-pressed="rating === value"
        @click="handleSelect(value)"
      >
        {{ value }}
      </button>

      <span v-if="value === 1 || value === 10" class="number_rating__cell__label">
        {{ value === 1 ? t("general.rating.bad") : t("general.rating.good") }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.number_rating {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;

  &__cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-1;

    &__item {
      @include colors.color(content, high);
      @include text.general(low, l);
      @include radius(s);

      width: $space-10;
      height: $space-10;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      border: 1px solid;
      cursor: pointer;
    }

    &__label {
      @include text.general(low, xs);
      @include text.style(italic);
    }
  }
}
</style>
